import { fetchTimeseriesSample } from '@api/dataSource';

/**
 * Format a Unix timestamp (seconds) to a readable date string
 * @param {number} timestamp - Unix timestamp in seconds
 * @returns {string} - Formatted date string
 */
function formatTimestamp(timestamp) {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Transform sample endpoint response to timeseries widget format
 * Returns entityMap and raw timeseries data for TimeseriesWidget
 *
 * @param {Array} entities - Array of {id, name, hostName?}
 * @param {Object} timeseries - Object keyed by entity ID with array of {timestamp, value}
 * @param {string} groupBy - 'metrics' or 'hosts'
 * @param {number} limit - Max points per entity
 * @returns {Object|null} - Data with entityMap and timeseries
 */
function transformSampleToTimeseriesFormat(entities, timeseries, groupBy, limit) {
  if (!entities || entities.length === 0) {
    return null;
  }

  const timeseriesData = {};
  const entityMap = {};

  entities.forEach((entity) => {
    const refidKey = String(entity.id);
    const points = timeseries[refidKey] || [];

    // Limit points
    timeseriesData[refidKey] = points.slice(0, limit);

    let displayName = entity.name;
    if (groupBy === 'metrics' && entity.hostName) {
      displayName = `${entity.hostName} - ${entity.name}`;
    }

    entityMap[refidKey] = {
      id: entity.id,
      name: entity.name,
      displayName,
    };
  });

  return {
    entityMap,
    timeseries: timeseriesData,
  };
}

/**
 * Transform sample endpoint response to chart format
 * Similar to transformTimeseriesData but works with the sample endpoint response
 *
 * @param {Array} entities - Array of {id, name, hostName?}
 * @param {Object} timeseries - Object keyed by entity ID with array of {timestamp, value}
 * @param {string} groupBy - 'metrics' or 'hosts'
 * @returns {Object|null} - Chart data with categories and series
 */
function transformSampleToChartFormat(entities, timeseries, groupBy) {
  if (!entities || entities.length === 0) {
    return null;
  }

  // Collect all unique timestamps for categories
  const timestampSet = new Set();
  Object.values(timeseries).forEach((points) => {
    if (Array.isArray(points)) {
      points.forEach((point) => {
        if (point.timestamp != null) {
          timestampSet.add(point.timestamp);
        }
      });
    }
  });

  // Keep raw timestamps sorted for data alignment
  const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

  if (timestamps.length === 0) {
    return null;
  }

  // Build series for each entity
  const series = entities.map((entity) => {
    const entityPoints = timeseries[String(entity.id)] || [];

    // Create a map for quick timestamp lookup
    const pointMap = new Map();
    entityPoints.forEach((point) => {
      pointMap.set(point.timestamp, point.value);
    });

    // Build data array aligned with raw timestamps
    const data = timestamps.map((timestamp) => {
      const value = pointMap.get(timestamp);
      return value !== undefined ? value : null;
    });

    // Build display name
    let displayName = entity.name;
    if (groupBy === 'metrics' && entity.hostName) {
      displayName = `${entity.hostName} - ${entity.name}`;
    }

    return {
      name: displayName,
      data,
      refid: entity.id,
    };
  });

  // Format timestamps for display (e.g., "Jan 15, 10:30 AM")
  const categories = timestamps.map(formatTimestamp);

  return {
    categories,
    series,
  };
}

/**
 * Loads metric values data from InfluxDB for preview mode in the editor
 * Uses the /timeseries/sample endpoint which:
 * 1. Queries InfluxDB to find entities that actually have recent data
 * 2. Applies user permissions (CTE-based RLS)
 * 3. Returns sample entities with their timeseries data
 *
 * This replaces the old approach of loading ALL entities via /filters/all
 * and randomly selecting some (which might not have data).
 *
 * @param {Object} widget - The widget configuration
 * @param {number} previewLimit - Number of sample entities to use for preview (default 3)
 * @param {Object} options - Additional options
 * @param {string} options.format - Output format: 'chart' (default) for line widgets, 'timeseries' for timeseries widgets
 * @param {number} options.pointsLimit - Max points per entity for timeseries format (default 100)
 * @returns {Promise<Object|null>} - Transformed data or null if no data
 */
export const loadMetricValuesDataForPreview = async (widget, previewLimit = 3, options = {}) => {
  const { format = 'chart', pointsLimit = 100 } = options;

  try {
    // Get groupBy from metric_values data source config (defaults to 'metrics')
    // This determines whether we query by hosts or metrics
    const metricValuesDs = (widget.config?.dataSources || []).find((ds) => ds.type === 'metric_values');
    const groupBy = metricValuesDs?.config?.groupBy || 'metrics';

    // Use the new /timeseries/sample endpoint that returns entities WITH data
    const response = await fetchTimeseriesSample({
      isHost: groupBy === 'hosts',
      limit: previewLimit,
      lookback: '24h',
      aggregateLiteral: metricValuesDs?.config?.aggregateLiteral || '5m',
      aggregateFunction: metricValuesDs?.config?.aggregateFunction || 'mean',
    });

    if (response.status !== 'success' || !response.data) {
      return null;
    }

    const { entities, timeseries } = response.data;

    if (!entities || entities.length === 0) {
      return null;
    }

    // Transform based on requested format
    if (format === 'timeseries') {
      return transformSampleToTimeseriesFormat(entities, timeseries, groupBy, pointsLimit);
    }

    // Default: chart format for line widgets
    return transformSampleToChartFormat(entities, timeseries, groupBy);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[loadMetricValuesDataForPreview] Failed to load preview:', error?.message || error);
    return null;
  }
};
